import React, { useState } from 'react'
import bankQuestionService from '../../services/bankQuestionService_moi'
import { QuizScoringService } from '../../services/quiz/QuizScoringService'

const AnswerFieldTest: React.FC = () => {
  const [testResults, setTestResults] = useState<any[]>([])
  const [loading, setLoading] = useState(false)

  const testAnswerFields = async () => {
    setLoading(true)
    setTestResults([])

    try {
      console.log('🧪 Testing answer field reading for different question types...')

      // Test với L11, C1, B01 - các loại câu hỏi TN, DS, TL
      const testCases = [
        { lop: '11', chuong: '1', bai: '01', type: 'TN', description: 'Trắc nghiệm' },
        { lop: '11', chuong: '1', bai: '01', type: 'DS', description: 'Đúng/Sai' },
        { lop: '11', chuong: '1', bai: '01', type: 'TL', description: 'Tự luận' }
      ]

      const results = []

      for (const testCase of testCases) {
        try {
          console.log(`🔍 Testing ${testCase.type} questions...`)

          // Lấy tất cả câu hỏi có sẵn
          const allQuestions = await bankQuestionService.getAllAvailableQuestions(
            testCase.lop,
            testCase.chuong,
            testCase.bai
          )

          const questions = allQuestions[testCase.type as keyof typeof allQuestions]
          console.log(`📊 Found ${questions.length} ${testCase.type} questions`)

          if (questions.length > 0) {
            // Lấy 3 câu đầu tiên để test
            const sampleQuestions = questions.slice(0, 3)

            for (const question of sampleQuestions) {
              // Tạo đáp án test
              let testAnswer: any = null
              if (testCase.type === 'TN') {
                testAnswer = 'A' // Test với đáp án A
              } else if (testCase.type === 'DS') {
                testAnswer = { 
                  [`${question.id}_statement_0`]: true,
                  [`${question.id}_statement_1`]: false,
                  [`${question.id}_statement_2`]: true,
                  [`${question.id}_statement_3`]: false
                }
              } else if (testCase.type === 'TL') {
                testAnswer = '0.5' // Test với đáp án số
              }

              // Test chấm điểm
              const scoringResult = QuizScoringService.scoreQuiz(
                [question],
                { [question.id]: testAnswer },
                60, // 1 phút
                70  // 70% để đạt
              )

              results.push({
                type: testCase.type,
                description: testCase.description,
                questionId: question.id,
                questionText: question.cau_hoi?.substring(0, 100) + '...',
                correctAnswer: question.dap_an_dung,
                correctAnswerType: typeof question.dap_an_dung,
                testAnswer,
                testAnswerType: typeof testAnswer,
                scoringResult: {
                  score: scoringResult.score,
                  maxScore: scoringResult.maxScore,
                  percentage: scoringResult.percentage,
                  isCorrect: scoringResult.answers[0]?.isCorrect || false
                },
                rawQuestion: question // Để debug
              })
            }
          } else {
            results.push({
              type: testCase.type,
              description: testCase.description,
              error: `Không tìm thấy câu hỏi ${testCase.type}`,
              questionCount: 0
            })
          }

        } catch (error) {
          console.error(`❌ Error testing ${testCase.type}:`, error)
          results.push({
            type: testCase.type,
            description: testCase.description,
            error: error.message,
            questionCount: 0
          })
        }
      }

      setTestResults(results)
      console.log('✅ Answer field testing complete:', results)

    } catch (error) {
      console.error('❌ Error in answer field testing:', error)
      setTestResults([{
        error: `Global error: ${error.message}`,
        type: 'ERROR'
      }])
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Answer Field Reading Test</h1>
      
      <div className="mb-6">
        <button
          onClick={testAnswerFields}
          disabled={loading}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
        >
          {loading ? 'Testing...' : 'Test Answer Field Reading'}
        </button>
      </div>

      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded">
        <h3 className="font-semibold mb-2">🎯 Test Purpose:</h3>
        <ul className="text-sm space-y-1">
          <li>• Test if TL questions correctly read <code className="bg-white px-1 rounded">answer_value</code> field</li>
          <li>• Test if TN questions correctly read <code className="bg-white px-1 rounded">correct_answer</code> field</li>
          <li>• Test if DS questions correctly read answer fields</li>
          <li>• Verify scoring logic works with correct answer fields</li>
        </ul>
      </div>

      {testResults.length > 0 && (
        <div className="space-y-6">
          {testResults.map((result, index) => (
            <div key={index} className="border border-gray-300 rounded-lg p-6 bg-white">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">
                  {result.type} - {result.description}
                </h2>
                {result.scoringResult && (
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                    result.scoringResult.isCorrect 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {result.scoringResult.isCorrect ? '✅ Correct' : '❌ Incorrect'}
                  </div>
                )}
              </div>

              {result.error ? (
                <div className="bg-red-50 border border-red-200 rounded p-4">
                  <div className="text-red-800 font-medium">Error:</div>
                  <div className="text-red-600">{result.error}</div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Question Info */}
                  <div className="bg-gray-50 rounded p-4">
                    <div className="font-medium mb-2">Question ID: {result.questionId}</div>
                    <div className="text-sm text-gray-600 mb-2">
                      {result.questionText}
                    </div>
                  </div>

                  {/* Answer Comparison */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Correct Answer from DB */}
                    <div className="bg-green-50 border border-green-200 rounded p-4">
                      <div className="font-medium text-green-800 mb-2">
                        📚 Correct Answer from DB:
                      </div>
                      <div className="font-mono text-sm bg-white p-2 rounded border">
                        {JSON.stringify(result.correctAnswer, null, 2)}
                      </div>
                      <div className="text-xs text-green-600 mt-1">
                        Type: {result.correctAnswerType}
                      </div>
                    </div>

                    {/* Test Answer */}
                    <div className="bg-blue-50 border border-blue-200 rounded p-4">
                      <div className="font-medium text-blue-800 mb-2">
                        🧪 Test Answer:
                      </div>
                      <div className="font-mono text-sm bg-white p-2 rounded border">
                        {JSON.stringify(result.testAnswer, null, 2)}
                      </div>
                      <div className="text-xs text-blue-600 mt-1">
                        Type: {result.testAnswerType}
                      </div>
                    </div>
                  </div>

                  {/* Scoring Result */}
                  {result.scoringResult && (
                    <div className="bg-purple-50 border border-purple-200 rounded p-4">
                      <div className="font-medium text-purple-800 mb-2">
                        🎯 Scoring Result:
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-gray-600">Score:</div>
                          <div className="font-bold">{result.scoringResult.score}/{result.scoringResult.maxScore}</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Percentage:</div>
                          <div className="font-bold">{result.scoringResult.percentage}%</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Is Correct:</div>
                          <div className={`font-bold ${result.scoringResult.isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                            {result.scoringResult.isCorrect ? 'Yes' : 'No'}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-600">Status:</div>
                          <div className={`font-bold ${result.scoringResult.isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                            {result.scoringResult.isCorrect ? 'PASS' : 'FAIL'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Raw Question Data (for debugging) */}
                  <details className="bg-gray-50 rounded p-4">
                    <summary className="font-medium text-gray-700 cursor-pointer">
                      🔍 Raw Question Data (Debug)
                    </summary>
                    <pre className="mt-2 text-xs bg-white p-2 rounded border overflow-auto max-h-40">
                      {JSON.stringify(result.rawQuestion, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Expected Results Guide */}
      <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded">
        <h3 className="font-semibold mb-2">📋 Expected Results:</h3>
        <ul className="text-sm space-y-1">
          <li>• <strong>TL questions:</strong> Should read <code className="bg-white px-1 rounded">answer_value</code> field correctly</li>
          <li>• <strong>TN questions:</strong> Should read <code className="bg-white px-1 rounded">correct_answer</code> or <code className="bg-white px-1 rounded">answer_key</code> field</li>
          <li>• <strong>DS questions:</strong> Should read answer fields and support multi-statement scoring</li>
          <li>• <strong>Scoring:</strong> TN/TL = 1 point if correct, DS = partial scoring (0.1, 0.25, 0.5, 1.0)</li>
        </ul>
      </div>
    </div>
  )
}

export default AnswerFieldTest